'use client';

import { logger } from '@magicschool/logger';
import { DEFAULT_LOCALE, type Language, languages } from '@magicschool/supabase/types';
import { useEffect, useRef } from 'react';
import { useIntl } from 'react-intl';
import { TRANSLATION_REFRESH_TRACKER_COOKIE } from '@/features/languages/utils';
import { useLocale } from '@/hooks/useLocale';
import { analyticsTrack } from '@/util/amplitude/analyticsClient';
import { deleteCookie, getCookieValue, setCookie } from '@/util/cookies';
import { setPersistedToast } from '@/util/persistedToast';

function getNormalizedLocale(locale: string): Language {
  const lowerLocale = locale.toLowerCase();

  if (lowerLocale === 'en') {
    return DEFAULT_LOCALE;
  }

  // https://stackoverflow.com/questions/5074769/cyanogenmod-translate-a-project/8470980#8470980
  if (lowerLocale === 'iw') {
    return 'he';
  }

  if (lowerLocale === 'es') {
    return 'es-us';
  }

  return locale as Language;
}

/**
 * Origin: https://github.com/facebook/react/issues/11538#issuecomment-417504600
 *
 * This patch protects the app in a scenario when the DOM language was changed (likely due to Google/Chrome Translate)
 *
 * Write up: https://martijnhols.nl/blog/everything-about-google-translate-crashing-react
 **/
export default function LanguagePatchClient() {
  const intl = useIntl();
  const { locale, onLocaleChange } = useLocale();
  const observerRef = useRef<MutationObserver | null>(null);
  const hasRefreshedPrior = getCookieValue(TRANSLATION_REFRESH_TRACKER_COOKIE);

  useEffect(() => {
    // Mutation Observer Callback
    const handleDOMMutations = () => {
      // We utilize react-intl and cookies to manage lang, changes to documentLang are from translators
      if (document.documentElement.lang !== locale) {
        // Chrome, Google translate - uses 'en' for English and we have two types - this defaults for the user to en-us;
        const newLocale = getNormalizedLocale(document.documentElement.lang);
        const isSupportedLanguage = languages.includes(newLocale);

        // Track the translation event
        analyticsTrack('Browser:Translation', {
          locale: newLocale,
          originalLocale: document.documentElement.lang,
          isSupportedLanguage: isSupportedLanguage,
        });

        /**
         * If attempting to translate the application into a language we do NOT support
         */
        if (!isSupportedLanguage) {
          logger.error('Invalid locale detected', { newLocale });
          /**
           * Prevent infinite loop incases of scenarios such as "always translate..."
           */
          if (!hasRefreshedPrior) {
            setPersistedToast('error', intl.formatMessage({ id: 'translation.not-supported' }));
            setCookie(TRANSLATION_REFRESH_TRACKER_COOKIE, 'true');
            window.location.reload();
          }
          return;
        }

        /**
         * - Update locale
         * - Delete previous cookie (toast can be triggered again)
         */
        onLocaleChange(newLocale);
        deleteCookie(TRANSLATION_REFRESH_TRACKER_COOKIE);
      }
    };

    // Setup the observer
    const observer = new MutationObserver(handleDOMMutations);
    observerRef.current = observer;

    // Start observing the DOM
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['lang'],
      childList: false,
      characterData: false,
    });

    // Cleanup on unmount
    return () => {
      observer.disconnect();
      observerRef.current = null;
    };
  }, [intl, locale, hasRefreshedPrior, onLocaleChange]);

  return null;
}
